import { FormEvent, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { appPublicUrl } from "../lib/firebase";
import {
  buildSessionDeliverablePreview,
  listSessionDeliverables,
  listSessionEmailJobs,
  queueSessionDeliverableEmails,
  requestSessionDeliverables,
} from "../services/deliverableService";
import {
  listSessionAttendanceStatuses,
  listSessionAttendances,
  markParticipantAbsent,
  resolveAttendanceWindow,
  type AttendanceWindow,
} from "../services/attendanceService";
import { listAccessibleFormations } from "../services/formationService";
import {
  addExpectedParticipant,
  addSessionTrainer,
  archiveTrainingSession,
  attachFormationToSession,
  closeTrainingSession,
  createTrainingSession,
  defaultSessionChecklist,
  finalizeTrainingSession,
  freezeSessionContent,
  listAccessibleTrainingSessions,
  listExpectedParticipants,
  listSessionFormations,
  listSessionTrainers,
  startTrainingSession,
  updateTrainingSessionMetadata,
} from "../services/sessionService";
import {
  activateSessionShortCode,
  disableSessionShortCode,
  listSessionShortCodeAccessRequests,
} from "../services/sessionShortCodeService";
import type { Formation } from "../types/formation";
import type { AttendanceRecord, AttendanceStatus } from "../types/access";
import type {
  SessionDeliverable,
  SessionDeliverablePreview,
  SessionEmailJob,
} from "../types/deliverables";
import type {
  ExpectedParticipant,
  SessionChecklist,
  SessionFormationAttachment,
  SessionShortCodeAccessRequest,
  SessionTrainer,
  SessionTrainerRole,
  TrainingSession,
} from "../types/session";

function formatDateTimeLocal(value: Date | null): string {
  if (!value) {
    return "";
  }

  const offsetMs = value.getTimezoneOffset() * 60_000;
  return new Date(value.getTime() - offsetMs).toISOString().slice(0, 16);
}

function parseDateTimeLocal(value: string): Date | null {
  return value ? new Date(value) : null;
}

function formatDate(value: Date | null): string {
  if (!value) {
    return "Non disponible";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

function checklistEntries(checklist: SessionChecklist) {
  return [
    ["companyAgreement", "Convention validée"],
    ["roomReady", "Salle prête"],
    ["materialsReady", "Supports prêts"],
    ["participantListReady", "Liste participants prête"],
    ["accessibilityChecked", "Accessibilité vérifiée"],
  ] as const;
}

function buildJoinUrl(shortCode: string): string {
  const url = new URL(appPublicUrl);
  url.hash = `/join?code=${encodeURIComponent(shortCode)}`;
  return url.toString();
}

function buildFormationPublicationUrl(formationId: string): string {
  const params = new URLSearchParams({
    contentPanel: "publication",
    formation: formationId,
    formationTab: "content",
    workspace: "formations",
  });

  return `#/admin?${params.toString()}`;
}

function isClosedForSupport(session: TrainingSession | null): boolean {
  return session?.status === "ended" || session?.status === "archived";
}

function missingPublishedSnapshotFormationId(message: string): string | null {
  const match = /^La formation ([^ ]+) n'a aucun snapshot publié/.exec(message);

  return match?.[1] ?? null;
}

type SessionEditorMode = "create" | "edit";

function deliverableStatusLabel(status: SessionDeliverable["status"]): string {
  if (status === "generated") {
    return "Généré";
  }

  if (status === "generating") {
    return "Génération";
  }

  if (status === "failed") {
    return "Erreur";
  }

  return "En attente backend";
}

function emailJobStatusLabel(status: SessionEmailJob["status"]): string {
  if (status === "sent") {
    return "Envoyé";
  }

  if (status === "sending") {
    return "Envoi";
  }

  if (status === "failed") {
    return "Erreur";
  }

  return "En attente backend";
}

export function SessionPreparationWorkspace() {
  const { claims, isGlobalAdmin, user } = useAuth();
  const [attachedFormations, setAttachedFormations] = useState<
    SessionFormationAttachment[]
  >([]);
  const [accessRequests, setAccessRequests] = useState<
    SessionShortCodeAccessRequest[]
  >([]);
  const [attendanceStatuses, setAttendanceStatuses] = useState<
    AttendanceStatus[]
  >([]);
  const [attendances, setAttendances] = useState<AttendanceRecord[]>([]);
  const [availableFormations, setAvailableFormations] = useState<Formation[]>([]);
  const [checklist, setChecklist] = useState<SessionChecklist>(
    defaultSessionChecklist,
  );
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyFirstName, setCompanyFirstName] = useState("");
  const [companyLastName, setCompanyLastName] = useState("");
  const [endAt, setEndAt] = useState("");
  const [deliverablePreview, setDeliverablePreview] =
    useState<SessionDeliverablePreview | null>(null);
  const [deliverables, setDeliverables] = useState<SessionDeliverable[]>([]);
  const [emailJobs, setEmailJobs] = useState<SessionEmailJob[]>([]);
  const [formationToAttach, setFormationToAttach] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [participantEmail, setParticipantEmail] = useState("");
  const [participantFirstName, setParticipantFirstName] = useState("");
  const [participantLastName, setParticipantLastName] = useState("");
  const [participants, setParticipants] = useState<ExpectedParticipant[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [sessionSearch, setSessionSearch] = useState("");
  const [sessionEditorMode, setSessionEditorMode] =
    useState<SessionEditorMode | null>(null);
  const [startResolutionFormationId, setStartResolutionFormationId] =
    useState<string | null>(null);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [startAt, setStartAt] = useState("");
  const [timezone, setTimezone] = useState("Europe/Paris");
  const [title, setTitle] = useState("");
  const [trainerRole, setTrainerRole] = useState<SessionTrainerRole>("trainer");
  const [trainerUid, setTrainerUid] = useState("");
  const [trainers, setTrainers] = useState<SessionTrainer[]>([]);

  const selectedSession = useMemo(
    () => sessions.find((session) => session.id === selectedSessionId) ?? null,
    [selectedSessionId, sessions],
  );
  const filteredSessions = useMemo(() => {
    const search = sessionSearch.trim().toLowerCase();

    if (!search) {
      return sessions;
    }

    return sessions.filter((session) =>
      `${session.title} ${session.companyTrainingManagerEmail} ${session.status}`
        .toLowerCase()
        .includes(search),
    );
  }, [sessionSearch, sessions]);
  const mayCreateSession = isGlobalAdmin || claims.canCreateSession;
  const attendanceWindow = useMemo<AttendanceWindow | null>(() => {
    if (!selectedSession || selectedSession.status !== "active") {
      return null;
    }

    return resolveAttendanceWindow({
      endAt: selectedSession.endAt,
      startAt: selectedSession.startAt,
      timezone: selectedSession.timezone,
    });
  }, [selectedSession]);
  const attendanceReviewedParticipantIds = useMemo(
    () =>
      new Set([
        ...attendances.map((attendance) => attendance.participantSlotId),
        ...attendanceStatuses.map((status) => status.participantSlotId),
      ]),
    [attendanceStatuses, attendances],
  );
  const missingAttendanceReviewCount = Math.max(
    0,
    participants.length - attendanceReviewedParticipantIds.size,
  );

  useEffect(() => {
    if (!user) {
      setSessions([]);
      setAvailableFormations([]);
      setSessionEditorMode(null);
      return;
    }

    setIsLoading(true);
    void Promise.all([
      listAccessibleTrainingSessions({
        isGlobalAdmin,
        uid: user.uid,
      }),
      listAccessibleFormations({
        isGlobalAdmin,
        uid: user.uid,
      }),
    ])
      .then(([nextSessions, nextFormations]) => {
        setSessions(nextSessions);
        setAvailableFormations(nextFormations);
        setSelectedSessionId((currentId) =>
          currentId && nextSessions.some((session) => session.id === currentId)
            ? currentId
            : nextSessions[0]?.id ?? null,
        );
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger les sessions préparées.");
      })
      .finally(() => setIsLoading(false));
  }, [claims.canCreateSession, isGlobalAdmin, user]);

  useEffect(() => {
    if (!selectedSession) {
      setTitle("");
      setCompanyEmail("");
      setCompanyFirstName("");
      setCompanyLastName("");
      setStartAt("");
      setEndAt("");
      setTimezone("Europe/Paris");
      setChecklist(defaultSessionChecklist);
      setAttachedFormations([]);
      setAccessRequests([]);
      setAttendanceStatuses([]);
      setAttendances([]);
      setDeliverablePreview(null);
      setDeliverables([]);
      setEmailJobs([]);
      setTrainers([]);
      setParticipants([]);
      return;
    }

    setTitle(selectedSession.title);
    setCompanyEmail(selectedSession.companyTrainingManagerEmail);
    setCompanyFirstName(selectedSession.companyTrainingManagerFirstName);
    setCompanyLastName(selectedSession.companyTrainingManagerLastName);
    setStartAt(formatDateTimeLocal(selectedSession.startAt));
    setEndAt(formatDateTimeLocal(selectedSession.endAt));
    setTimezone(selectedSession.timezone);
    setChecklist(selectedSession.checklist);

    void Promise.all([
      listSessionFormations(selectedSession.id),
      listSessionTrainers(selectedSession.id),
      listExpectedParticipants(selectedSession.id),
      selectedSession.shortCodeStatus === "active"
        ? listSessionShortCodeAccessRequests(selectedSession.shortCode)
        : Promise.resolve([]),
      selectedSession.status === "active" || isClosedForSupport(selectedSession)
        ? listSessionAttendances(selectedSession.id)
        : Promise.resolve([]),
      selectedSession.status === "active" || isClosedForSupport(selectedSession)
        ? listSessionAttendanceStatuses(selectedSession.id)
        : Promise.resolve([]),
      isClosedForSupport(selectedSession)
        ? buildSessionDeliverablePreview(selectedSession)
        : Promise.resolve(null),
      isClosedForSupport(selectedSession)
        ? listSessionDeliverables(selectedSession.id)
        : Promise.resolve([]),
      isClosedForSupport(selectedSession)
        ? listSessionEmailJobs(selectedSession.id)
        : Promise.resolve([]),
    ])
      .then(
        ([
          nextFormations,
          nextTrainers,
          nextParticipants,
          nextAccessRequests,
          nextAttendances,
          nextAttendanceStatuses,
          nextDeliverablePreview,
          nextDeliverables,
          nextEmailJobs,
        ]) => {
        setAttachedFormations(nextFormations);
        setTrainers(nextTrainers);
        setParticipants(nextParticipants);
        setAccessRequests(nextAccessRequests);
        setAttendances(nextAttendances);
        setAttendanceStatuses(nextAttendanceStatuses);
        setDeliverablePreview(nextDeliverablePreview);
        setDeliverables(nextDeliverables);
        setEmailJobs(nextEmailJobs);
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger le détail de la session.");
      });
  }, [selectedSession]);

  function buildDraft() {
    return {
      checklist,
      companyTrainingManagerEmail: companyEmail,
      companyTrainingManagerFirstName: companyFirstName,
      companyTrainingManagerLastName: companyLastName,
      endAt: parseDateTimeLocal(endAt),
      startAt: parseDateTimeLocal(startAt),
      timezone,
      title,
    };
  }

  async function handleSaveSession(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!user) {
      return;
    }

    setIsSaving(true);
    setMessage(null);
    setStartResolutionFormationId(null);

    try {
      const nextSession = selectedSession
        ? await updateTrainingSessionMetadata({
            actorUid: user.uid,
            draft: buildDraft(),
            sessionId: selectedSession.id,
          })
        : await createTrainingSession({
            actorUid: user.uid,
            draft: buildDraft(),
          });

      setSessions((current) => [
        nextSession,
        ...current.filter((session) => session.id !== nextSession.id),
      ]);
      setSelectedSessionId(nextSession.id);
      setSessionEditorMode(null);
      setMessage(selectedSession ? "Session mise à jour." : "Session créée.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible d'enregistrer la session.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAttachFormation() {
    if (!user || !selectedSession || !formationToAttach) {
      return;
    }

    const formation = availableFormations.find(
      (item) => item.id === formationToAttach,
    );

    if (!formation) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const attachment = await attachFormationToSession({
        actorUid: user.uid,
        formationId: formation.id,
        session: selectedSession,
        title: formation.title,
      });
      setAttachedFormations((current) => [
        ...current.filter((item) => item.formationId !== attachment.formationId),
        attachment,
      ]);
      setFormationToAttach("");
      setMessage("Formation attachée à la session.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible d'attacher cette formation.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAddTrainer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!user || !selectedSession) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const trainer = await addSessionTrainer({
        actorUid: user.uid,
        role: trainerRole,
        session: selectedSession,
        trainerUid,
      });
      setTrainers((current) => [
        trainer,
        ...current.filter((item) => item.uid !== trainer.uid),
      ]);
      setTrainerUid("");
      setMessage("Formateur ajouté.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible d'ajouter ce formateur.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAddParticipant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!user || !selectedSession) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const participant = await addExpectedParticipant({
        actorUid: user.uid,
        draft: {
          email: participantEmail,
          firstName: participantFirstName,
          lastName: participantLastName,
        },
        sessionId: selectedSession.id,
      });
      setParticipants((current) => [participant, ...current]);
      setParticipantEmail("");
      setParticipantFirstName("");
      setParticipantLastName("");
      setMessage("Participant attendu ajouté.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible d'ajouter ce participant.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleFinalize() {
    if (!user || !selectedSession) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const nextSession = await finalizeTrainingSession({
        actorUid: user.uid,
        session: selectedSession,
      });
      setSessions((current) =>
        current.map((session) =>
          session.id === nextSession.id ? nextSession : session,
        ),
      );
      setMessage("Session finalisée en statut planned.");
    } catch (error) {
      console.error(error);
      setMessage(
        error instanceof Error
          ? error.message
          : "Impossible de finaliser cette session.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleStartSession() {
    if (!user || !selectedSession) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const activation = await startTrainingSession({
        actorUid: user.uid,
        session: selectedSession,
      });
      setSessions((current) =>
        current.map((session) =>
          session.id === activation.session.id ? activation.session : session,
        ),
      );
      setMessage(
        `Session démarrée : ${activation.resourceCount} ressource(s) figée(s).`,
      );
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Impossible de démarrer cette session.";
      setMessage(
        errorMessage,
      );
      setStartResolutionFormationId(
        missingPublishedSnapshotFormationId(errorMessage),
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleFreezeSessionContent() {
    if (!user || !selectedSession) {
      return;
    }

    setIsSaving(true);
    setMessage(null);
    setStartResolutionFormationId(null);

    try {
      const result = await freezeSessionContent({
        actorUid: user.uid,
        session: selectedSession,
      });
      setMessage(
        `Contenus figés : ${result.snapshotCount}/${result.formationCount} formation(s), ${result.publishedVersionCount} version(s) publiée(s), ${result.refreshedCount} snapshot(s) actualisé(s).`,
      );
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Impossible de figer les contenus de la session.";
      setMessage(errorMessage);
      setStartResolutionFormationId(
        missingPublishedSnapshotFormationId(errorMessage),
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleCloseSession() {
    if (!user || !selectedSession) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const closure = await closeTrainingSession({
        actorUid: user.uid,
        session: selectedSession,
      });
      setSessions((current) =>
        current.map((session) =>
          session.id === closure.session.id ? closure.session : session,
        ),
      );
      setAccessRequests([]);
      setMessage(
        closure.summary.deliverablesStatus === "ready_for_generation"
          ? "Session clôturée. Les livrables peuvent être préparés."
          : `Session clôturée. ${closure.summary.participantWithoutAttendanceCount} participant(s) restent à vérifier côté émargement.`,
      );
    } catch (error) {
      console.error(error);
      setMessage(
        error instanceof Error
          ? error.message
          : "Impossible de clôturer cette session.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleArchiveSession() {
    if (!user || !selectedSession) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const nextSession = await archiveTrainingSession({
        actorUid: user.uid,
        session: selectedSession,
      });
      setSessions((current) =>
        current.map((session) =>
          session.id === nextSession.id ? nextSession : session,
        ),
      );
      setMessage("Session archivée. Elle reste consultable pour le support.");
    } catch (error) {
      console.error(error);
      setMessage(
        error instanceof Error
          ? error.message
          : "Impossible d'archiver cette session.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRequestDeliverables() {
    if (!user || !selectedSession) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const nextDeliverables = await requestSessionDeliverables({
        actorUid: user.uid,
        session: selectedSession,
      });
      const nextPreview = await buildSessionDeliverablePreview(selectedSession);
      setDeliverables(nextDeliverables);
      setDeliverablePreview(nextPreview);
      setMessage("Jobs PDF préparés pour le backend.");
    } catch (error) {
      console.error(error);
      setMessage(
        error instanceof Error
          ? error.message
          : "Impossible de préparer les livrables.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleQueueEmails() {
    if (!user || !selectedSession) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const nextEmailJobs = await queueSessionDeliverableEmails({
        actorUid: user.uid,
        session: selectedSession,
      });
      setEmailJobs(nextEmailJobs);
      setMessage("Jobs email créés pour le backend.");
    } catch (error) {
      console.error(error);
      setMessage(
        error instanceof Error
          ? error.message
          : "Impossible de créer les jobs email.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleActivateShortCode() {
    if (!user || !selectedSession) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const nextSession = await activateSessionShortCode({
        actorUid: user.uid,
        session: selectedSession,
      });
      const nextAccessRequests = await listSessionShortCodeAccessRequests(
        nextSession.shortCode,
      );
      setSessions((current) =>
        current.map((session) =>
          session.id === nextSession.id ? nextSession : session,
        ),
      );
      setAccessRequests(nextAccessRequests);
      setMessage("Clé courte activée pour l'accueil participant.");
    } catch (error) {
      console.error(error);
      setMessage(
        error instanceof Error
          ? error.message
          : "Impossible d'activer la clé courte.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDisableShortCode() {
    if (!user || !selectedSession) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const nextSession = await disableSessionShortCode({
        actorUid: user.uid,
        session: selectedSession,
      });
      setSessions((current) =>
        current.map((session) =>
          session.id === nextSession.id ? nextSession : session,
        ),
      );
      setAccessRequests([]);
      setMessage("Clé courte désactivée.");
    } catch (error) {
      console.error(error);
      setMessage(
        error instanceof Error
          ? error.message
          : "Impossible de désactiver la clé courte.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleMarkAbsent(participantSlotId: string) {
    if (!user || !selectedSession || !attendanceWindow) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const status = await markParticipantAbsent({
        actorUid: user.uid,
        attendanceWindow,
        participantSlotId,
        sessionId: selectedSession.id,
      });
      setAttendanceStatuses((current) => [
        status,
        ...current.filter((item) => item.id !== status.id),
      ]);
      setMessage("Participant marqué absent pour la demi-journée.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible de marquer ce participant absent.");
    } finally {
      setIsSaving(false);
    }
  }

  function openCreateSessionEditor() {
    setSessionEditorMode("create");
    setSelectedSessionId(null);
    setTitle("");
    setCompanyEmail("");
    setCompanyFirstName("");
    setCompanyLastName("");
    setStartAt("");
    setEndAt("");
    setTimezone("Europe/Paris");
    setChecklist(defaultSessionChecklist);
    setMessage(null);
  }

  function openEditSessionEditor(session: TrainingSession) {
    setSessionEditorMode("edit");
    setSelectedSessionId(session.id);
    setTitle(session.title);
    setCompanyEmail(session.companyTrainingManagerEmail);
    setCompanyFirstName(session.companyTrainingManagerFirstName);
    setCompanyLastName(session.companyTrainingManagerLastName);
    setStartAt(formatDateTimeLocal(session.startAt));
    setEndAt(formatDateTimeLocal(session.endAt));
    setTimezone(session.timezone);
    setChecklist(session.checklist);
    setMessage(null);
  }

  function closeSessionEditor() {
    setSessionEditorMode(null);
    setSelectedSessionId((currentId) => currentId ?? sessions[0]?.id ?? null);
  }

  const currentAttendanceIds = new Set(
    attendanceWindow
      ? attendances
          .filter(
            (attendance) =>
              attendance.sessionDate === attendanceWindow.sessionDate &&
              attendance.halfDay === attendanceWindow.halfDay,
          )
          .map((attendance) => attendance.participantSlotId)
      : [],
  );
  const currentAbsentIds = new Set(
    attendanceWindow
      ? attendanceStatuses
          .filter(
            (status) =>
              status.sessionDate === attendanceWindow.sessionDate &&
              status.halfDay === attendanceWindow.halfDay &&
              status.status === "absent",
          )
          .map((status) => status.participantSlotId)
      : [],
  );

  return (
    <section className="session-workspace">
      <article className="panel panel-wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Sessions</p>
            <h2>Préparation</h2>
          </div>
          <button
            className="button button-primary"
            disabled={!mayCreateSession}
            onClick={openCreateSessionEditor}
            type="button"
          >
            Nouvelle session
          </button>
        </div>
        <div className="list-toolbar">
          <label className="field list-filter">
            <span>Filtrer</span>
            <input
              onChange={(event) => setSessionSearch(event.target.value)}
              placeholder="Titre, responsable ou statut"
              type="search"
              value={sessionSearch}
            />
          </label>
          <div className="list-summary">
            <strong>{filteredSessions.length}</strong>
            <span>session(s)</span>
          </div>
        </div>
        {isLoading ? <p>Chargement des sessions...</p> : null}
        {filteredSessions.length > 0 ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Session</th>
                  <th scope="col">Statut</th>
                  <th scope="col">Début</th>
                  <th scope="col">Responsable</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.map((session) => (
                  <tr
                    className={session.id === selectedSessionId ? "row-selected" : ""}
                    key={session.id}
                  >
                    <td>
                      <button
                        className="table-title-button"
                        onClick={() => setSelectedSessionId(session.id)}
                        type="button"
                      >
                        <strong>{session.title}</strong>
                        <span>{session.shortCode}</span>
                      </button>
                    </td>
                    <td>{session.status}</td>
                    <td>{formatDate(session.startAt)}</td>
                    <td>{session.companyTrainingManagerEmail || "Non renseigné"}</td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="button button-secondary button-small"
                          onClick={() => setSelectedSessionId(session.id)}
                          type="button"
                        >
                          Ouvrir
                        </button>
                        <button
                          className="button button-secondary button-small"
                          onClick={() => openEditSessionEditor(session)}
                          type="button"
                        >
                          Modifier
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Aucune session ne correspond aux filtres.</p>
        )}
      </article>

      <article className="panel panel-wide">
        <p className="eyebrow">Pilotage session</p>
        <h2>{selectedSession?.title ?? "Aucune session sélectionnée"}</h2>
        {selectedSession ? (
          <>
            <div className="capability-strip">
              <span>Clé courte : {selectedSession.shortCode}</span>
              <span>Statut clé : {selectedSession.shortCodeStatus}</span>
              <span>Session : {selectedSession.status}</span>
              <span>Tag contenu : {selectedSession.contentTagId ?? "Non créé"}</span>
            </div>
            <div className="actions">
              <button
                className="button button-secondary"
                disabled={isSaving}
                onClick={() => openEditSessionEditor(selectedSession)}
                type="button"
              >
                Modifier les métadonnées
              </button>
              <button
                className="button button-secondary"
                disabled={isSaving || selectedSession.status !== "draft"}
                onClick={handleFinalize}
                type="button"
              >
                Finaliser en planned
              </button>
              <button
                className="button button-primary"
                disabled={isSaving || selectedSession.status !== "planned"}
                onClick={handleStartSession}
                type="button"
              >
                Démarrer la session
              </button>
              <button
                className="button button-secondary"
                disabled={isSaving || selectedSession.status !== "active"}
                onClick={handleCloseSession}
                type="button"
              >
                Clôturer la session
              </button>
              <button
                className="button button-secondary"
                disabled={isSaving || selectedSession.status !== "ended"}
                onClick={handleArchiveSession}
                type="button"
              >
                Archiver
              </button>
            </div>
          </>
        ) : (
          <p>Ouvrez une session depuis la liste ou créez une nouvelle session.</p>
        )}
        {selectedSession?.status === "active" ? (
          <div className="actions">
            <a
              className="button button-secondary"
              href={buildJoinUrl(selectedSession.shortCode)}
              rel="noreferrer"
              target="_blank"
            >
              Ouvrir l'accueil participant
            </a>
            <button
              className="button button-primary"
              disabled={isSaving || selectedSession.shortCodeStatus === "active"}
              onClick={handleActivateShortCode}
              type="button"
            >
              Activer la clé courte
            </button>
            <button
              className="button button-secondary"
              disabled={isSaving || selectedSession.shortCodeStatus !== "active"}
              onClick={handleDisableShortCode}
              type="button"
            >
              Désactiver la clé courte
            </button>
          </div>
        ) : null}
        {selectedSession &&
        (selectedSession.status === "active" || isClosedForSupport(selectedSession)) ? (
          <div className="closure-summary">
            <p className="eyebrow">Clôture</p>
            <div className="capability-strip">
              <span>{attendances.length} émargement(s)</span>
              <span>{attendanceStatuses.length} absence(s)</span>
              <span>{missingAttendanceReviewCount} participant(s) à vérifier</span>
              <span>
                Livrables :{" "}
                {missingAttendanceReviewCount === 0
                  ? "prêts à préparer"
                  : "à vérifier"}
              </span>
            </div>
          </div>
        ) : null}
        {message ? (
          <div className="alert alert-info" role="status">
            {message}
            {startResolutionFormationId ? (
              <div className="alert-actions">
                <a
                  className="button button-secondary button-small"
                  href={buildFormationPublicationUrl(startResolutionFormationId)}
                >
                  Ouvrir la publication
                </a>
              </div>
            ) : null}
          </div>
        ) : null}
      </article>

      {sessionEditorMode ? (
        <div className="modal-backdrop">
          <section
            aria-labelledby="session-editor-title"
            aria-modal="true"
            className="modal-panel modal-panel-large"
            role="dialog"
          >
            <div className="section-heading">
              <div>
                <p className="eyebrow">Métadonnées session</p>
                <h2 id="session-editor-title">
                  {sessionEditorMode === "create" ? "Créer une session" : "Modifier la session"}
                </h2>
              </div>
              <button
                className="button button-secondary button-small"
                disabled={isSaving}
                onClick={closeSessionEditor}
                type="button"
              >
                Fermer
              </button>
            </div>
            <form className="stack" onSubmit={handleSaveSession}>
              <div className="form-grid">
                <label className="field">
                  <span>Titre</span>
                  <input
                    disabled={sessionEditorMode === "create" && !mayCreateSession}
                    onChange={(event) => setTitle(event.target.value)}
                    required
                    type="text"
                    value={title}
                  />
                </label>
                <label className="field">
                  <span>Timezone</span>
                  <input
                    onChange={(event) => setTimezone(event.target.value)}
                    type="text"
                    value={timezone}
                  />
                </label>
                <label className="field">
                  <span>Début</span>
                  <input
                    onChange={(event) => setStartAt(event.target.value)}
                    type="datetime-local"
                    value={startAt}
                  />
                </label>
                <label className="field">
                  <span>Fin</span>
                  <input
                    onChange={(event) => setEndAt(event.target.value)}
                    type="datetime-local"
                    value={endAt}
                  />
                </label>
              </div>
              <div className="form-grid">
                <label className="field">
                  <span>Email responsable entreprise</span>
                  <input
                    onChange={(event) => setCompanyEmail(event.target.value)}
                    type="email"
                    value={companyEmail}
                  />
                </label>
                <label className="field">
                  <span>Prénom responsable</span>
                  <input
                    onChange={(event) => setCompanyFirstName(event.target.value)}
                    type="text"
                    value={companyFirstName}
                  />
                </label>
                <label className="field">
                  <span>Nom responsable</span>
                  <input
                    onChange={(event) => setCompanyLastName(event.target.value)}
                    type="text"
                    value={companyLastName}
                  />
                </label>
              </div>
              <div className="check-grid">
                {checklistEntries(checklist).map(([key, label]) => (
                  <label className="checkbox-field" key={key}>
                    <input
                      checked={checklist[key]}
                      onChange={(event) =>
                        setChecklist((current) => ({
                          ...current,
                          [key]: event.target.checked,
                        }))
                      }
                      type="checkbox"
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <div className="actions">
                <button
                  className="button button-primary"
                  disabled={
                    isSaving ||
                    (sessionEditorMode === "create" && !mayCreateSession)
                  }
                  type="submit"
                >
                  {sessionEditorMode === "create" ? "Créer" : "Enregistrer"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      <article className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Formations attachées</p>
            <h2>Programme session</h2>
          </div>
          <button
            className="button button-secondary"
            disabled={
              !selectedSession ||
              attachedFormations.length === 0 ||
              isSaving ||
              (selectedSession.status !== "draft" &&
                selectedSession.status !== "planned")
            }
            onClick={handleFreezeSessionContent}
            type="button"
          >
            Figer les contenus
          </button>
        </div>
        <div className="inline-form">
          <label className="field">
            <span>Formation</span>
            <select
              disabled={!selectedSession}
              onChange={(event) => setFormationToAttach(event.target.value)}
              value={formationToAttach}
            >
              <option value="">Sélectionner</option>
              {availableFormations.map((formation) => (
                <option key={formation.id} value={formation.id}>
                  {formation.title}
                </option>
              ))}
            </select>
          </label>
          <button
            className="button button-primary"
            disabled={!selectedSession || !formationToAttach || isSaving}
            onClick={handleAttachFormation}
            type="button"
          >
            Attacher
          </button>
        </div>
        {attachedFormations.length > 0 ? (
          <ul className="resource-list">
            {attachedFormations.map((formation) => (
              <li key={formation.formationId}>
                <strong>{formation.title}</strong>
                <span>Ordre {formation.order}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p>Aucune formation attachée.</p>
        )}
      </article>

      <article className="panel">
        <p className="eyebrow">Formateurs</p>
        <h2>Équipe session</h2>
        <form className="inline-form" onSubmit={handleAddTrainer}>
          <label className="field">
            <span>UID formateur</span>
            <input
              disabled={!selectedSession}
              onChange={(event) => setTrainerUid(event.target.value)}
              type="text"
              value={trainerUid}
            />
          </label>
          <label className="field">
            <span>Rôle</span>
            <select
              disabled={!selectedSession}
              onChange={(event) =>
                setTrainerRole(event.target.value as SessionTrainerRole)
              }
              value={trainerRole}
            >
              <option value="trainer">Formateur</option>
              <option value="sessionAdmin">Admin session</option>
            </select>
          </label>
          <button
            className="button button-primary"
            disabled={!selectedSession || !trainerUid.trim() || isSaving}
            type="submit"
          >
            Ajouter
          </button>
        </form>
        {trainers.length > 0 ? (
          <ul className="resource-list">
            {trainers.map((trainer) => (
              <li key={trainer.uid}>
                <strong>{trainer.uid}</strong>
                <span>{trainer.role}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p>Aucun formateur ajouté.</p>
        )}
      </article>

      <article className="panel panel-wide">
        <p className="eyebrow">Participants attendus</p>
        <h2>Liste d'accueil</h2>
        <form className="inline-form" onSubmit={handleAddParticipant}>
          <label className="field">
            <span>Prénom</span>
            <input
              disabled={!selectedSession}
              onChange={(event) => setParticipantFirstName(event.target.value)}
              type="text"
              value={participantFirstName}
            />
          </label>
          <label className="field">
            <span>Nom</span>
            <input
              disabled={!selectedSession}
              onChange={(event) => setParticipantLastName(event.target.value)}
              type="text"
              value={participantLastName}
            />
          </label>
          <label className="field">
            <span>Email optionnel</span>
            <input
              disabled={!selectedSession}
              onChange={(event) => setParticipantEmail(event.target.value)}
              type="email"
              value={participantEmail}
            />
          </label>
          <button
            className="button button-primary"
            disabled={
              !selectedSession ||
              !participantFirstName.trim() ||
              !participantLastName.trim() ||
              isSaving
            }
            type="submit"
          >
            Ajouter
          </button>
        </form>
        {participants.length > 0 ? (
          <ul className="admin-list">
            {participants.map((participant) => (
              <li key={participant.id}>
                <div>
                  <strong>
                    {participant.firstName} {participant.lastName}
                  </strong>
                  <span>
                    {participant.email || "Email non renseigné"} -{" "}
                    {participant.onboardingStatus}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>Aucun participant attendu.</p>
        )}
      </article>

      {selectedSession?.status === "active" && attendanceWindow ? (
        <article className="panel panel-wide">
          <p className="eyebrow">Émargement</p>
          <h2>
            {attendanceWindow.label} - {attendanceWindow.displayDate}
          </h2>
          <div className="capability-strip">
            <span>{currentAttendanceIds.size} présent(s)</span>
            <span>{currentAbsentIds.size} absent(s)</span>
            <span>
              {Math.max(
                0,
                participants.length -
                  currentAttendanceIds.size -
                  currentAbsentIds.size,
              )}{" "}
              en attente
            </span>
          </div>
          {participants.length > 0 ? (
            <ul className="admin-list">
              {participants.map((participant) => {
                const isPresent = currentAttendanceIds.has(participant.id);
                const isAbsent = currentAbsentIds.has(participant.id);
                const status = isPresent
                  ? "Présent"
                  : isAbsent
                    ? "Absent"
                    : "En attente";
                const statusClass = isPresent
                  ? "status status-accepted"
                  : isAbsent
                    ? "status status-revoked"
                    : "status status-pending";

                return (
                  <li key={`attendance-${participant.id}`}>
                    <div>
                      <strong>
                        {participant.firstName} {participant.lastName}
                      </strong>
                      <span>{participant.email || "Email non renseigné"}</span>
                    </div>
                    <div className="row-actions">
                      <span className={statusClass}>{status}</span>
                      {!isPresent && !isAbsent ? (
                        <button
                          className="button button-secondary button-small"
                          disabled={isSaving}
                          onClick={() => handleMarkAbsent(participant.id)}
                          type="button"
                        >
                          Marquer absent
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>Aucun participant attendu.</p>
          )}
        </article>
      ) : null}

      {isClosedForSupport(selectedSession) ? (
        <article className="panel panel-wide">
          <p className="eyebrow">Livrables post-formation</p>
          <h2>Preview PDF et emails</h2>
          {deliverablePreview ? (
            <>
              <div className="capability-strip">
                <span>{deliverablePreview.deckCount} support(s)</span>
                <span>
                  {deliverablePreview.questionnaireAnswerCount} réponse(s)
                  questionnaire
                </span>
                <span>
                  {deliverablePreview.exerciseAnswerCount} réponse(s) exercice
                </span>
                <span>{deliverablePreview.correctionCount} corrigé(s)</span>
                <span>{deliverablePreview.signedAttendanceCount} signature(s)</span>
                <span>{deliverablePreview.absenceCount} absence(s)</span>
                <span>
                  {deliverablePreview.participantEmailCount}/
                  {deliverablePreview.participantCount} email(s) participant
                </span>
              </div>
              {deliverablePreview.issues.length > 0 ? (
                <div className="alert alert-warning" role="status">
                  {deliverablePreview.issues.join(" ")}
                </div>
              ) : (
                <div className="alert alert-info" role="status">
                  Données prêtes pour génération backend.
                </div>
              )}
              <div className="actions">
                <button
                  className="button button-primary"
                  disabled={isSaving || selectedSession?.status === "archived"}
                  onClick={handleRequestDeliverables}
                  type="button"
                >
                  Préparer les jobs PDF
                </button>
                <button
                  className="button button-secondary"
                  disabled={
                    isSaving ||
                    deliverables.length === 0 ||
                    selectedSession?.status === "archived"
                  }
                  onClick={handleQueueEmails}
                  type="button"
                >
                  Créer les jobs email
                </button>
              </div>
              <div className="deliverables-grid">
                <section>
                  <h3>Documents prévus</h3>
                  <ul className="resource-list">
                    {deliverablePreview.deliverables.map((deliverable) => (
                      <li key={deliverable.id}>
                        <strong>{deliverable.title}</strong>
                        <span>
                          {deliverable.recipientLabel} - {deliverable.type}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
                <section>
                  <h3>Jobs PDF</h3>
                  {deliverables.length > 0 ? (
                    <ul className="resource-list">
                      {deliverables.map((deliverable) => (
                        <li key={deliverable.id}>
                          <strong>{deliverable.title}</strong>
                          <span>
                            {deliverableStatusLabel(deliverable.status)} -{" "}
                            {deliverable.storagePath ?? "PDF non généré"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>Aucun job PDF préparé.</p>
                  )}
                </section>
                <section>
                  <h3>Jobs email</h3>
                  {emailJobs.length > 0 ? (
                    <ul className="resource-list">
                      {emailJobs.map((job) => (
                        <li key={job.id}>
                          <strong>{job.type}</strong>
                          <span>
                            {emailJobStatusLabel(job.status)} -{" "}
                            {job.recipients.join(", ")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>Aucun job email créé.</p>
                  )}
                </section>
              </div>
            </>
          ) : (
            <p>Preview des livrables indisponible.</p>
          )}
        </article>
      ) : null}

      {selectedSession?.shortCodeStatus === "active" ? (
        <article className="panel panel-wide">
          <p className="eyebrow">Accueil participant</p>
          <h2>Demandes d'accès</h2>
          {accessRequests.length > 0 ? (
            <ul className="admin-list">
              {accessRequests.map((request) => (
                <li key={request.id}>
                  <div>
                    <strong>
                      {request.firstName} {request.lastName}
                    </strong>
                    <span>
                      {request.email} -{" "}
                      {request.participantSlotId
                        ? `participant ${request.participantSlotId}`
                        : "hors liste"}
                    </span>
                  </div>
                  <span className="status status-pending">En attente</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>Aucune demande d'accès pour cette clé.</p>
          )}
        </article>
      ) : null}
    </section>
  );
}
